<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="fair-survey-step__options">
      <template
        v-for="occasion in step.occasions"
        :key="occasion.value"
      >
        <button
          type="button"
          class="fair-survey-step__option"
          :class="{
            'fair-survey-step__option--selected':
              selectedOccasion === occasion.value,
          }"
          :aria-pressed="selectedOccasion === occasion.value"
          @click="pickOccasion(occasion.value)"
        >
          <FairRadioButtonIndicator :selected="selectedOccasion === occasion.value" />
          <span class="fair-survey-step__option-label">{{ occasion.label }}</span>
        </button>

        <div
          v-if="
            selectedOccasion === occasion.value
              && occasion.subOptions
              && occasion.subOptions.length
          "
          class="gift-step__sub"
        >
          <FairDropdown
            :model-value="selectedSubOptions[occasion.value] ?? null"
            :options="occasion.subOptions.map((option) => ({
              key: option.value,
              label: option.label,
            }))"
            :placeholder="`Выберите ${occasion.label.toLowerCase()}`"
            show-radio
            @update:model-value="(value) => updateSubOption(occasion.value, value)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { FairDropdown } from '@/ui/primitives/FairDropdown'
import { FairRadioButtonIndicator } from '@/ui/primitives/FairRadioButton'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'occasion' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const selectedOccasion = ref<string | null>(null)
// Храним выбранные дополнительные опции для каждого случая: { occasionValue: subOptionValue }
const selectedSubOptions = ref<Record<string, string | null>>({})

function pickOccasion(value: string) {
  selectedOccasion.value = value
  updateOccasion()
}

function updateOccasion() {
  giftStore.updateGiftData(props.step.id, selectedOccasion.value)
  
  // Очищаем subOptions для случая, который больше не выбран
  if (selectedOccasion.value) {
    // Очищаем все subOptions кроме текущего выбранного случая
    Object.keys(selectedSubOptions.value).forEach(occasionValue => {
      if (occasionValue !== selectedOccasion.value) {
        selectedSubOptions.value[occasionValue] = null
        giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, null)
        delete selectedSubOptions.value[occasionValue]
      }
    })
  } else {
    // Если ничего не выбрано, очищаем все subOptions
    Object.keys(selectedSubOptions.value).forEach(occasionValue => {
      selectedSubOptions.value[occasionValue] = null
      giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, null)
    })
    selectedSubOptions.value = {}
  }
}

function updateSubOption(occasionValue: string, value: string | null) {
  selectedSubOptions.value[occasionValue] = value
  giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, value)
}

onMounted(() => {
  const data = giftStore.giftModal.giftData
  const savedOccasion = data[props.step.id]
  selectedOccasion.value = savedOccasion || null
  
  // Загружаем сохраненные subOptions для выбранного случая
  if (selectedOccasion.value) {
    const savedValue = data[`${props.step.id}_${selectedOccasion.value}`]
    if (savedValue) {
      selectedSubOptions.value[selectedOccasion.value] = savedValue
    }
  }
})
</script>

<style scoped>
.gift-step__fields {
  display: flex;
  flex-direction: column;
  gap: var(--fair-rd-space-24);
}

.gift-step__field {
  display: flex;
  flex-direction: column;
  gap: var(--fair-rd-space-8);
}

.gift-step__label {
  font-family: var(--fair-rd-font-display, 'Gilroy', sans-serif);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--fair-rd-text-muted);
}

/* Sub-question belongs to the option above it, so it is indented under it. */
.gift-step__sub {
  padding: var(--fair-rd-space-8) 0 var(--fair-rd-space-8)
    var(--fair-rd-space-40);
}
</style>
